// src/components/Amboseli.jsx
import React from "react";
import { Helmet } from "react-helmet";
import useScrollToTop from "../ScrollToTop.js";
import { motion } from "framer-motion";
import Slider from "react-slick";
import { FaCampground, FaHotel } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./place.css";

const Amboseli = () => {
  useScrollToTop();
  const { t } = useTranslation("amboseli");
  const highlights = t("placeHighlights.highlights", { returnObjects: true });
  const tips = t("travelTips.tips", { returnObjects: true });
  const options = t("accommodation.options", { returnObjects: true });

  return (
    <motion.div
      className="place-page"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      {/* SEO Metadata */}
      <Helmet>
  <title>Amboseli National Park | Safari at the Foot of Kilimanjaro | Olenini Safaris</title>
  <meta
    name="description"
    content="Discover Amboseli National Park with Olenini Safaris — see majestic elephants, Mount Kilimanjaro views, and authentic African wildlife safaris in Kenya."
  />
  <link rel="canonical" href="https://oleninisafaris.com/amboseli" />
  <meta
    name="robots"
    content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
  />

  {/* Open Graph / Facebook */}
  <meta property="og:title" content="Amboseli National Park Safari | Olenini Safaris" />
  <meta
    property="og:description"
    content="Experience Amboseli National Park — Kenya’s land of elephants and Kilimanjaro backdrops. Tailor your safari adventure with Olenini Safaris."
  />
  <meta property="og:image" content="https://oleninisafaris.com/images/elephant.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/amboseli" />
  <meta property="og:type" content="place" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Amboseli National Park Safari | Olenini Safaris" />
  <meta
    name="twitter:description"
    content="Explore Amboseli — elephants, Kilimanjaro, and unforgettable wildlife safaris with Olenini Safaris."
  />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/elephant.jpg" />

  {/* JSON-LD Structured Data */}
  <script type="application/ld+json">
    {`
    {
      "@context": "https://schema.org",
      "@type": "NationalPark",
      "name": "Amboseli National Park",
      "description": "Amboseli National Park is located in southern Kenya, famous for its large elephant herds and stunning views of Mount Kilimanjaro.",
      "image": "https://oleninisafaris.com/images/elephant.jpg",
      "url": "https://oleninisafaris.com/amboseli",
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -2.6486,
        "longitude": 37.2605
      },
      "containedInPlace": {
        "@type": "TouristDestination",
        "name": "Kenya"
      },
      "provider": {
        "@type": "TravelAgency",
        "name": "Olenini Tours & Safaris",
        "url": "https://oleninisafaris.com"
      },
      "sameAs": [
        "https://en.wikipedia.org/wiki/Amboseli_National_Park",
        "https://www.google.com/maps/place/Amboseli+National+Park"
      ]
    }
    `}
  </script>
</Helmet>


      {/* Hero Section */}
      <section className="place-hero">
        <img
          src="/images/elephant1.jpg"
          alt="Elephants in Amboseli National Park with Mount Kilimanjaro"
          className="hero-image"
          loading="eager"
        />
        <div className="hero-text">
          <h1>{t("heroText.title")}</h1>
          <p>{t("heroText.description")}</p>
        </div>
      </section>

      {/* About Section */}
      <section className="place-about">
        <h2>{t("placeAbout.title")}</h2>
        <p dangerouslySetInnerHTML={{ __html: t("placeAbout.description") }} />
      </section>

      {/* Highlights Section */}
      <section className="place-highlights">
        <div className="card">
          <h2>{t("placeHighlights.title")}</h2>
          <ul>
            {highlights.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>

        <div className="card">
          <h3>{t("placeHighlights.subtitle")}</h3>
          <p>{t("placeHighlights.paragraph1")}</p>
          <p>{t("placeHighlights.paragraph2")}</p>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="place-highlights alt-layout">
        <div className="card unified-card">
          <div
            className="image-carousel"
            aria-label="Amboseli Wildlife Image Carousel"
          >
            <Slider
              autoplay
              autoplaySpeed={3000}
              infinite
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              pauseOnHover
              swipeToSlide
              adaptiveHeight={false}
            >
              <div>
                <img
                  src="/images/cheetah12.jpg"
                  alt="Cheetah walking across Amboseli plain"
                  loading="lazy"
                  className="slick-image"
                />
              </div>
              <div>
                <img
                  src="/images/elephants.jpg"
                  alt="Herd of elephants in Amboseli"
                  loading="lazy"
                  className="slick-image"
                />
              </div>
              <div>
                <img
                  src="/images/cubswatering.jpg"
                  alt="Lion cubs at a watering hole in Amboseli"
                  loading="lazy"
                  className="slick-image"
                />
              </div>
              <div>
                <img
                  src="/images/ambobird.jpg"
                  alt="Bird flying over Amboseli wetlands"
                  loading="lazy"
                  className="slick-image"
                />
              </div>
            </Slider>
          </div>

          <div className="text-content">
            <h3>{t("travelTips.title")}</h3>
            <ul>
              {tips.map((tip, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: tip }} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="place-gallery">
        <h2>Amboseli Safari Gallery</h2>
        <div className="gallery-grid">
          <img
            src="/images/DOMINATE.JPG"
            alt="Elephants and Kilimanjaro in Amboseli"
            loading="lazy"
          />
          <img
            src="/images/Elephants1.jpg"
            alt="Group of elephants near water"
            loading="lazy"
          />
          <img
            src="/images/birdflying.jpg"
            alt="Bird flying in Amboseli wetland"
            loading="lazy"
          />
        </div>
      </section>

      {/* Accommodation */}
      <section className="place-accommodation">
        <h2>{t("accommodation.title")}</h2>
        <p>{t("accommodation.intro")}</p>

        <div className="accommodation-options">
          <div className="option-card">
            <FaCampground className="icon" />
            <h3>{options[0].title}</h3>
            <p>{options[0].description}</p>
          </div>

          <div className="option-card">
            <FaHotel className="icon" />
            <h3>{options[1].title}</h3>
            <p>{options[1].description}</p>
          </div>
        </div>

        <p className="note">{t("accommodation.note")}</p>
      </section>

      {/* Call to Action */}
      <section className="place-cta" aria-label="Call to Action Section">
        <h2>{t("cta.title")}</h2>
        <p>{t("cta.description")}</p>
        <Link
          to="/contact?source=amboselidestination"
          className="book-now"
        >
          {t("cta.button")}
        </Link>
      </section>
    </motion.div>
  );
};

export default Amboseli;
